import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Heart, Leaf, Compass, ChevronRight, ShieldCheck } from "lucide-react";
import { FOCUS_TOPICS, FOCUS_LIST } from "@/lib/focusTopics";
import { BOOKING_URL } from "@/lib/constants";
import SEO from "@/components/SEO";

/**
 * Symptom-focus landing page. Renders compassionate, soft-language content
 * for a single topic (lower-back, neck/shoulder, sleep, headaches) — never
 * diagnostic, never prescriptive. Encourages the booking conversation as
 * the calm next step.
 */
export default function FocusPage() {
  const { slug } = useParams();
  const topic = FOCUS_TOPICS[slug];
  if (!topic) return <Navigate to="/focus" replace />;

  const related = (topic.relatedTopics || [])
    .map((s) => FOCUS_TOPICS[s])
    .filter(Boolean);

  return (
    <div className="bg-white" data-testid={`focus-page-${topic.slug}`}>
      <SEO
        title={topic.seoTitle}
        description={topic.seoDescription}
        path={`/focus/${topic.slug}`}
        image={topic.image}
      />

      {/* Hero */}
      <section className="bg-[#FAFAFA] border-b border-[#F3F4F6]">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]" data-testid="focus-eyebrow">
              {topic.eyebrow}
            </p>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#080808] leading-[1.05]" data-testid="focus-headline">
              {topic.headline}
            </h1>
            <p className="mt-6 text-lg text-[#3C4048] leading-relaxed max-w-xl" data-testid="focus-lead">
              {topic.lead}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={BOOKING_URL}
                data-testid="focus-cta-book"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#3A7A8C] text-white font-bold uppercase tracking-[0.12em] text-xs hover:bg-[#2E6573] transition-colors"
              >
                Begin your healing journey <ArrowRight size={14} />
              </a>
              <Link
                to="/contact"
                data-testid="focus-cta-talk"
                className="inline-flex items-center gap-2 px-6 py-3 border border-[#080808] text-[#080808] font-bold uppercase tracking-[0.12em] text-xs hover:bg-[#080808] hover:text-white transition-colors"
              >
                Talk to us first
              </Link>
            </div>
          </div>
          <div className="aspect-[5/4] overflow-hidden">
            <img
              src={topic.image}
              alt={topic.imageAlt}
              loading="lazy"
              className="w-full h-full object-cover"
              data-testid="focus-image"
            />
          </div>
        </div>
      </section>

      {/* Experience — empathy block */}
      <section className="max-w-[1080px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="flex items-start gap-3 mb-6">
          <Heart size={20} className="text-[#3A7A8C] mt-1 shrink-0" />
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]">Validation</p>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold text-[#080808] tracking-tight">{topic.experienceTitle}</h2>
          </div>
        </div>
        <ul className="space-y-3 pl-1 max-w-2xl" data-testid="focus-experience-list">
          {topic.experience.map((line, i) => (
            <li key={i} className="flex gap-3 text-[#3C4048] leading-relaxed">
              <ChevronRight size={16} className="text-[#5FA8B4] mt-1 shrink-0" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
        <p className="mt-8 text-sm text-[#6B7280] italic max-w-2xl">
          If any of those land for you, you&apos;re not alone — and you don&apos;t have to articulate it perfectly to begin.
        </p>
      </section>

      {/* Approach */}
      <section className="bg-[#F5F5F5] border-y border-[#F3F4F6]">
        <div className="max-w-[1080px] mx-auto px-5 md:px-10 py-16 md:py-24 grid md:grid-cols-[1fr_2fr] gap-12">
          <div>
            <Compass size={20} className="text-[#3A7A8C] mb-3" />
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]">Our approach</p>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold text-[#080808] tracking-tight">{topic.approachTitle}</h2>
          </div>
          <ul className="space-y-4" data-testid="focus-approach-list">
            {topic.approach.map((line, i) => (
              <li key={i} className="flex gap-3 text-[#3C4048] leading-relaxed">
                <span className="font-mono text-[#5FA8B4] shrink-0 text-sm mt-0.5">0{i + 1}</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Self-support tips */}
      <section className="max-w-[1080px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="flex items-start gap-3 mb-6">
          <Leaf size={20} className="text-[#3A7A8C] mt-1 shrink-0" />
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]">Between sessions</p>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold text-[#080808] tracking-tight">{topic.supportTitle}</h2>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4" data-testid="focus-support-list">
          {topic.support.map((tip, i) => (
            <div key={i} className="bg-white border border-[#E5E7EB] p-5">
              <p className="text-sm text-[#3C4048] leading-relaxed">{tip}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-xs text-[#6B7280] italic max-w-2xl flex items-start gap-2">
          <ShieldCheck size={14} className="text-[#3A7A8C] mt-px shrink-0" />
          These ideas are general wellness suggestions — not medical advice. If anything feels uncomfortable, please stop and consult your physician.
        </p>
      </section>

      {/* CTA */}
      <section className="bg-[#3A7A8C] text-white">
        <div className="max-w-[1080px] mx-auto px-5 md:px-10 py-16 md:py-20 text-center">
          <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white/70">{topic.ctaTitle}</p>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight max-w-2xl mx-auto leading-tight">
            A calm next step is here whenever you&apos;d like to take it.
          </h2>
          <p className="mt-5 text-white/90 max-w-2xl mx-auto leading-relaxed">
            {topic.ctaBody}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href={BOOKING_URL}
              data-testid="focus-cta-book-bottom"
              className="inline-flex items-center gap-2 px-7 py-3 bg-white text-[#3A7A8C] font-bold uppercase tracking-[0.12em] text-xs hover:bg-[#080808] hover:text-white transition-colors"
            >
              Book a session <ArrowRight size={14} />
            </a>
            <Link
              to="/team"
              data-testid="focus-cta-team"
              className="inline-flex items-center gap-2 px-7 py-3 border border-white/40 text-white font-bold uppercase tracking-[0.12em] text-xs hover:bg-white hover:text-[#3A7A8C] transition-colors"
            >
              Meet our Therapists
            </Link>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-[1080px] mx-auto px-5 md:px-10 py-16 md:py-20">
          <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]">Related focus areas</p>
          <h2 className="mt-2 text-2xl md:text-3xl font-bold text-[#080808] tracking-tight">
            Other things people come to us with
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="focus-related">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/focus/${r.slug}`}
                className="group bg-white border border-[#E5E7EB] p-6 hover:border-[#3A7A8C] transition-colors"
                data-testid={`focus-related-${r.slug}`}
              >
                <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]">{r.eyebrow}</p>
                <h3 className="mt-2 text-xl font-bold text-[#080808] group-hover:text-[#3A7A8C] transition-colors">{r.label}</h3>
                <p className="mt-3 text-sm text-[#6B7280] line-clamp-3">{r.lead.split(".")[0]}.</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.12em] text-[#3A7A8C]">
                  Read more <ArrowRight size={12} />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * Index page listing every focus topic — entry point at /focus.
 */
export function FocusIndex() {
  return (
    <div className="bg-white" data-testid="focus-index">
      <SEO
        title="Focus areas — Ashlan Clinic, Bermuda"
        description="Soft, evidence-aware support for the things our clients most often bring to us — lower-back tension, neck and shoulder, sleep, headaches. Calm conversation first, never diagnosis."
        path="/focus"
      />
      <section className="bg-[#FAFAFA] border-b border-[#F3F4F6]">
        <div className="max-w-[1080px] mx-auto px-5 md:px-10 py-16 md:py-20">
          <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]">Focus areas</p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-[#080808] leading-[1.05]">
            What our clients most often come to us with
          </h1>
          <p className="mt-5 text-lg text-[#3C4048] max-w-2xl leading-relaxed">
            Each one is a calm starting point — never a diagnosis, never a prescription. Choose whichever resonates and we&apos;ll listen from there.
          </p>
        </div>
      </section>
      <section className="max-w-[1080px] mx-auto px-5 md:px-10 py-16">
        <div className="grid sm:grid-cols-2 gap-5">
          {FOCUS_LIST.map((t) => (
            <Link
              key={t.slug}
              to={`/focus/${t.slug}`}
              className="group bg-white border border-[#E5E7EB] overflow-hidden hover:border-[#3A7A8C] transition-colors flex flex-col"
              data-testid={`focus-card-${t.slug}`}
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img src={t.image} alt={t.imageAlt} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]">{t.eyebrow}</p>
                <h2 className="mt-2 text-2xl font-bold text-[#080808] group-hover:text-[#3A7A8C] transition-colors">{t.label}</h2>
                <p className="mt-3 text-sm text-[#6B7280] line-clamp-3 flex-1">{t.lead}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.12em] text-[#3A7A8C]">
                  Explore <ArrowRight size={12} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
